import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Calculator, Globe } from "lucide-react";

export default function ImprovementCalculator({ category }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    project_type: "",
    size: 300,
    area_unit: "sqft",
    quality: "standard",
    country: "US",
    city: "National average",
  });

  useEffect(() => {
    api.get("/improvement-catalog").then(({ data }) => {
      setCatalog(data);
      const first = data.categories[category]?.projects[0]?.id || "";
      setForm((f) => ({ ...f, project_type: first }));
    }).catch(() => {});
  }, [category]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setResult(null); };

  const onCountry = (code) => {
    const c = catalog?.countries.find((x) => x.code === code);
    const unit = c?.unit === "metric" ? "sqm" : "sqft";
    setForm((f) => ({ ...f, country: code, area_unit: unit, city: "National average", size: unit === "sqm" ? 28 : 300 }));
    setResult(null);
  };

  const calc = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/public/improvement-estimate", {
        category,
        project_type: form.project_type,
        size: Number(form.size) || 0,
        area_unit: form.area_unit,
        quality: form.quality,
        country: form.country,
        city: form.city,
      });
      setResult(data);
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  if (!catalog) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const projects = catalog.categories[category]?.projects || [];
  const activeProj = projects.find((p) => p.id === form.project_type);
  const cityList = (catalog.cities && catalog.cities[form.country]) || [];
  const unitWord = form.area_unit === "sqm" ? "sq m" : "sq ft";
  const sym = result?.currency_symbol || "$";

  const field = "mt-1.5 rounded-sm border-border bg-transparent focus:border-primary focus:ring-primary";
  const lbl = "font-mono2 text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <div className="grid gap-0 border border-border lg:grid-cols-2" data-testid="improvement-calculator">
      <div className="border-b border-border p-8 lg:border-b-0 lg:border-r">
        <div className="mb-6 flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          <span className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Works anywhere in the world</span>
        </div>
        <div className="space-y-5">
          <div>
            <Label className={lbl}>Project</Label>
            <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
              <SelectTrigger data-testid="imp-project" className={field}><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={lbl}>Country</Label>
              <Select value={form.country} onValueChange={onCountry}>
                <SelectTrigger data-testid="imp-country" className={field}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {catalog.countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={lbl}>Quality</Label>
              <Select value={form.quality} onValueChange={(v) => set("quality", v)}>
                <SelectTrigger data-testid="imp-quality" className={field}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {catalog.qualities.map((q) => <SelectItem key={q} value={q} className="capitalize">{q}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={lbl}>{activeProj?.hint || "Size"} ({unitWord})</Label>
              <Input type="number" min={0} value={form.size} onChange={(e) => set("size", e.target.value)}
                data-testid="imp-size" className={field} />
            </div>
            <div>
              <Label className={lbl}>Units</Label>
              <Select value={form.area_unit} onValueChange={(v) => set("area_unit", v)}>
                <SelectTrigger data-testid="imp-unit" className={field}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sqft">Square feet</SelectItem>
                  <SelectItem value="sqm">Square metres</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {cityList.length > 1 && (
            <div>
              <Label className={lbl}>City</Label>
              <Select value={form.city} onValueChange={(v) => set("city", v)}>
                <SelectTrigger data-testid="imp-city" className={field}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {cityList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}


          <Button onClick={calc} disabled={loading || !form.project_type} data-testid="imp-run-btn"
            className="w-full rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Calculating…</> : <><Calculator className="mr-2 h-4 w-4" /> Estimate Cost</>}
          </Button>
        </div>
      </div>

      <div className="relative flex flex-col justify-center bg-secondary/10 p-8">
        <div className="absolute inset-0 bp-grid-fine opacity-40" />
        <div className="relative">
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div key="r" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="imp-result">
                <p className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">{result.project} · {result.country} · {result.currency}</p>
                <div className="mt-2 font-heading text-4xl font-extrabold tracking-tighter text-primary sm:text-5xl" data-testid="imp-range">
                  {money(result.low, sym)} – {money(result.high, sym)}
                </div>
                <div className="mt-6 grid grid-cols-2 gap-px border border-border bg-border">
                  <div className="bg-background p-4">
                    <div className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">Typical (mid)</div>
                    <div className="mt-1 font-mono2 text-lg font-bold">{money(result.mid, sym)}</div>
                  </div>
                  <div className="bg-background p-4">
                    <div className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">Per {result.unit_label}</div>
                    <div className="mt-1 font-mono2 text-lg font-bold">{result.cost_per_unit != null ? money(result.cost_per_unit, sym) : "—"}</div>
                  </div>
                </div>
                <ul className="mt-4 space-y-1">
                  {result.notes.map((n, i) => (
                    <li key={i} className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">· {n}</li>
                  ))}
                </ul>
                {result.fx_source === "live" && (
                  <p className="mt-2 flex items-center gap-1.5 font-mono2 text-[11px] uppercase tracking-widest text-primary" data-testid="imp-fx-badge">
                    <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" /> Live rates · updated {new Date((result.fx_updated || 0) * 1000).toLocaleDateString()}
                  </p>
                )}
              </motion.div>
            ) : (
              <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center">
                <Calculator className="mx-auto h-10 w-10 text-muted-foreground/40" />
                <p className="mt-4 max-w-xs font-heading text-lg text-muted-foreground">
                  Pick a project and your country, then hit <span className="text-primary">Estimate Cost</span> for a local price range.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
